import React from 'react';
import { Link } from 'react-router-dom';
import { useAppType } from '../provider/AppTypeProvider';
import { Logo } from '../components/images';
import { 
  FaGraduationCap, 
  FaBuilding, 
  FaSearch, 
  FaBell, 
  FaUser, 
  FaChartLine,
  FaEnvelope,
  FaDiscord,
  FaMobile,
  FaArrowRight,
  FaCheckCircle,
  FaStar,
  FaUsers,
  FaRocket
} from 'react-icons/fa';
import MainHeader from './utils/MainHeader'; 

const About = () => {
  const { switchAppType } = useAppType();
  return (
    <div className="min-h-screen w-full">
      {/* Header */}
      <MainHeader />

      {/* What is Oodles Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              What is Oodles Internships Platform?
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              A comprehensive web application designed to streamline the internship process for both 
              students and employers. We eliminate the traditional hassles of internship hunting and talent acquisition.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaSearch className="text-orange-500 text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Smart Matching</h3>
              <p className="text-gray-600">Connects the right students with the right opportunities.</p>
            </div>

            <div className="text-center p-6">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaBell className="text-orange-500 text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Real-time Updates</h3>
              <p className="text-gray-600">Stay informed with instant notifications about applications and opportunities.</p>
            </div>

            <div className="text-center p-6">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaUser className="text-orange-500 text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Comprehensive Profiles</h3>
              <p className="text-gray-600">Showcase your skills, experience, and aspirations with detailed profiles.</p>
            </div>

            <div className="text-center p-6">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaChartLine className="text-orange-500 text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Streamlined Process</h3>
              <p className="text-gray-600">Simplified application and hiring process for maximum efficiency.</p>
            </div>
          </div>
        </div>
      </section>

      {/* For Students Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-6">
                <FaGraduationCap className="text-orange-500 text-3xl mr-3" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800">For Students</h2>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Easy Registration</h3>
                    <p className="text-gray-600">Quick signup with your UG email and build your digital resume.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Profile Building</h3>
                    <p className="text-gray-600">Showcase your education, skills, experience, and career goals.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Smart Search</h3>
                    <p className="text-gray-600">Filter opportunities by location, industry, duration, and skills.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Application Tracking</h3>
                    <p className="text-gray-600">Monitor your applications in real-time with status updates.</p>
                  </div>
                </div>
              </div>

              <Link 
                to="/register"
                onClick={() => switchAppType('intern')} 
                className="inline-flex items-center mt-8 bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
              >
                Get Started as Student
                <FaArrowRight className="ml-2" />
              </Link>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold mb-6 text-center">Platform Features</h3>
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-orange-50 rounded-lg">
                  <FaSearch className="text-orange-500 mr-3" />
                  <span className="font-medium">Explore Opportunities</span>
                </div>
                <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                  <FaChartLine className="text-blue-500 mr-3" />
                  <span className="font-medium">Track Applications</span>
                </div>
                <div className="flex items-center p-3 bg-green-50 rounded-lg">
                  <FaBell className="text-green-500 mr-3" />
                  <span className="font-medium">Get Announcements</span>
                </div>
                <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                  <FaUser className="text-purple-500 mr-3" />
                  <span className="font-medium">Manage Profile</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Employers Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold mb-6 text-center">Employer Dashboard</h3>
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                  <FaBuilding className="text-orange-500 mr-3" />
                  <span className="font-medium">Company Profile Setup</span>
                </div>
                <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                  <FaUsers className="text-blue-500 mr-3" />
                  <span className="font-medium">Position Management</span>
                </div>
                <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                  <FaChartLine className="text-green-500 mr-3" />
                  <span className="font-medium">Application Analytics</span>
                </div>
                <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                  <FaStar className="text-purple-500 mr-3" />
                  <span className="font-medium">Candidate Database</span>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center mb-6">
                <FaBuilding className="text-orange-500 text-3xl mr-3" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800"> For Employers</h2>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Quick Verification</h3>
                    <p className="text-gray-600">Fast registration with admin approval within 1-2 business days.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Position Posting</h3>
                    <p className="text-gray-600">Create detailed internship positions with all necessary requirements.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Application Management</h3>
                    <p className="text-gray-600">Review, filter, and manage applications efficiently.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Analytics & Insights</h3>
                    <p className="text-gray-600">Track performance metrics and optimize your hiring process.</p>
                  </div>
                </div>
              </div>

              <Link 
                to="/employer/register"
                onClick={() => switchAppType('employer')}  
                className="inline-flex items-center mt-8 bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
              >
                Join as Employer
                <FaArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Access Section */}
      <section className="py-16 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <FaMobile className="text-orange-500 text-3xl mr-3" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800">Mobile Access</h2>
          </div>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            The platform is fully responsive and works perfectly on desktop computers, tablets, 
            smartphones, and all major browsers (Chrome, Firefox, Safari, Edge).
          </p>
          
          <div className="bg-white p-6 rounded-xl shadow-lg inline-block">
            <p className="text-lg font-semibold text-gray-800 mb-2">💡 Mobile Tip</p>
            <p className="text-gray-600">Add the platform to your home screen for quick access!</p>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              💬 Support & Contact
            </h2>
            <p className="text-xl text-gray-600">
              We're here to help you succeed! Reach out through any of these channels.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <FaEnvelope className="text-orange-500 text-3xl mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Email Support</h3>
              <p className="text-gray-600 mb-4">For detailed feedback and technical issues</p>
              <a 
                href="mailto:info.oodlestech@gmail.com" 
                className="text-orange-500 font-medium hover:text-orange-600"
              >
                info.oodlestech@gmail.com
              </a>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <FaDiscord className="text-purple-500 text-3xl mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Discord Community</h3>
              <p className="text-gray-600 mb-4">Join our community for real-time help and peer support</p>
              <a
                href="https://discord.gg/2K6zpbJb5y"
                className="text-purple-500 font-medium hover:text-purple-600">
                Join Community
              </a>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <FaGraduationCap className="text-blue-500 text-3xl mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">University Coordinator</h3>
              <p className="text-gray-600 mb-4">University of Ghana School of Engineering Sciences</p>
              <a 
                href="mailto:jtportuphy@ug.edu.gh" 
                className="text-blue-500 font-medium hover:text-blue-600"
              >
                Joseph Tetteh Portuphy
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <FaRocket className="text-4xl mr-3" />
            <h2 className="text-3xl md:text-4xl font-bold">
              Ready to get started?
            </h2>
          </div>
          
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Let's revolutionize the internship experience together! Join the University of Ghana 
            School of Engineering Sciences pilot program today.
          </p>
          <p className="text-sm mt-6 opacity-90">
            This pilot program is specifically designed for the University of Ghana School of Engineering Sciences.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <img src={Logo} className="w-6 h-6 mr-2" alt="Logo"/>
            <span className="text-lg font-semibold">Oodles Internships</span>
          </div>
          <p className="text-gray-400 mb-4">
            Shaping the future of internships
          </p>
          <p className="text-sm text-gray-500">
            University of Ghana School of Engineering Sciences - Pilot Program 2025
          </p>
        </div>
      </footer>
    </div>
  );
};

export default About;